// Funnel instrumentation for the DCMA checker, reported to HubSpot as custom
// behavioral events. Events are pushed onto the HubSpot analytics queue
// (`window._hsq`), which the HubSpot tracking code drains once it loads. If the
// tracking code never loads, the pushes simply accumulate on the queue and are
// harmless -- so every function here is a safe no-op outside the browser and
// never throws into the widget's render path.

// Ordered funnel steps for the DCMA quality check tool. These are the raw step
// names; `trackFunnelEvent` qualifies them into HubSpot internal names
// (`pe{HubID}_{name}`), so each event configured in the HubSpot portal must use
// the matching `{name}` suffix.
export type DcmaFunnelEvent =
  | 'dcma_file_uploaded'
  | 'dcma_parse_complete'
  | 'dcma_parse_error'
  | 'dcma_results_viewed'
  | 'dcma_form_started'
  | 'dcma_form_submit'
  | 'dcma_form_error'
  | 'dcma_full_results_viewed';

export type FunnelEventProperties = Record<string, string | number | boolean>;

type HubSpotQueue = unknown[];

declare global {
  interface Window {
    _hsq?: HubSpotQueue;
  }
}

// HubSpot's tracking loader uses this id, so reusing it lets us detect a script
// the host page (or an earlier mount) already injected and avoid duplicates.
export const HUBSPOT_TRACKING_SCRIPT_ID = 'hs-script-loader';

// Portal id captured from `ensureHubSpotTracking`, used to qualify event names.
// HubSpot identifies custom behavioral events by their internal name, formatted
// `pe{HubID}_{name}`, so the raw funnel step name alone will not match the event
// definition in the portal.
let hubSpotPortalId: string | null = null;

function getHubSpotQueue(): HubSpotQueue | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const queue = window._hsq ?? [];
  window._hsq = queue;
  return queue;
}

// Ensure the HubSpot tracking code for the given portal is present on the page.
// The widget can be embedded on pages that do not already load HubSpot tracking,
// so this makes funnel instrumentation self-sufficient. It is idempotent: the
// script is injected at most once regardless of how many widgets mount.
export function ensureHubSpotTracking(portalId: string): void {
  if (typeof document === 'undefined') {
    return;
  }
  const trimmed = portalId.trim();
  if (!trimmed) {
    return;
  }
  hubSpotPortalId = trimmed;
  if (document.getElementById(HUBSPOT_TRACKING_SCRIPT_ID)) {
    return;
  }

  const script = document.createElement('script');
  script.id = HUBSPOT_TRACKING_SCRIPT_ID;
  script.type = 'text/javascript';
  script.async = true;
  script.defer = true;
  script.src = `https://js.hs-scripts.com/${encodeURIComponent(trimmed)}.js`;
  document.head.appendChild(script);
}

// Resolve the HubSpot internal name for a funnel step. HubSpot requires custom
// behavioral events to be pushed under their `pe{HubID}_{name}` internal name;
// without a known portal id we fall back to the raw name (a harmless no-op,
// since tracking is disabled in that case).
function qualifyEventName(event: DcmaFunnelEvent): string {
  return hubSpotPortalId ? `pe${hubSpotPortalId}_${event}` : event;
}

// Report a funnel step to HubSpot as a custom behavioral event.
export function trackFunnelEvent(event: DcmaFunnelEvent, properties?: FunnelEventProperties): void {
  const queue = getHubSpotQueue();
  if (!queue) {
    return;
  }
  queue.push(['trackCustomBehavioralEvent', { name: qualifyEventName(event), properties: properties ?? {} }]);
}
